import React, { useMemo } from 'react';
import cn from 'classnames';
import { Treatment } from 'libs/end-to-end/src/treatment';
import { TreatmentRecommendation } from 'libs/end-to-end/src/treatment';
import { TREATMENT_MESSAGE_IDS } from 'apps/sigma2-ui/src/constants/data';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { TreatmentMetaDetails } from 'apps/sigma2-ui/src/components/treatment/TreatmentMetaDetails';
import { TreatmentRecommendationPill } from 'apps/sigma2-ui/src/components/ui/TreatmentRecommendationPill';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { formatCurrency } from 'apps/sigma2-ui/src/lib/number-formatting';
import { IconChevronRightSvg } from 'apps/sigma2-ui/src/components/svg/IconChevronRight';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

// TODO: Refactor to consolidate logic with /FocusAreaPage/KeyTakeawaySolutionsCardStack.tsx
interface ProjectedSavingsCalloutProps {
  projectedSavings: NonNullable<Required<Treatment['projectedSavings']>>;
}

const ProjectedSavingsCallout: React.FC<ProjectedSavingsCalloutProps> = ({
  projectedSavings,
}) => {
  return (
    <div
      style={{
        background: 'hsla(212, 100%, 11%, 0.5)',
      }}
      className="absolute left-0 py-2 px-6 rounded-tr rounded-br max-w-[95%]"
    >
      <p className="text-sm">
        {/* TODO use language hook */}
        <strong>Projected Savings:</strong>
      </p>

      <p>
        ~{formatCurrency(projectedSavings.min)} -{' '}
        {formatCurrency(projectedSavings.max)} {projectedSavings.reason}
      </p>
    </div>
  );
};

const solutionCardGradient = (treatment: Treatment): string => {
  switch (treatment.recommendations[0]) {
    case TreatmentRecommendation.Values.BiggestImpact:
      return 'radial-gradient(112.91% 137.83% at 115.91% 137.83%, #0A56A2 0%, #012C57 72.37%, #012C57 100%)';

    case TreatmentRecommendation.Values.MostPopular:
      return 'radial-gradient(112.91% 137.83% at 115.91% 137.83%, #E73A90 0%, #012C57 72.37%, #012C57 100%)';

    case TreatmentRecommendation.Values.QuickestWin:
      return 'radial-gradient(112.91% 137.83% at 115.91% 137.83%, #45A265 0%, #012C57 72.37%, #012C57 100%)';

    default:
      return 'radial-gradient(112.91% 137.83% at 115.91% 137.83%, #45A265 0%, #012C57 72.37%, #012C57 100%)';
  }
};

interface SolutionCardProps {
  treatment: Treatment;
  onTreatmentTitleClick: () => void;
  allFocusAreaDefinitions: MyFocusAreaDefinition[];
}

const SolutionCard: React.FC<SolutionCardProps> = ({
  treatment,
  onTreatmentTitleClick,
  allFocusAreaDefinitions,
}) => {
  const { getLevelByColumnValue } = useDimensionConfig();

  const language = useLanguage();

  const levelDefinition = getLevelByColumnValue(
    treatment.dimension,
    treatment.level,
  );

  const focusAreaDefinition = useMemo(() => {
    return allFocusAreaDefinitions.find(
      (focusArea) => focusArea.focusArea === treatment.focusArea,
    );
  }, [allFocusAreaDefinitions, treatment]);

  if (!focusAreaDefinition) {
    return <></>;
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between">
        <S2Typography
          variant="headline-5-alt"
          textColorVariant="white"
          translatedContent={language.topSolutions()}
        />

        {!!treatment.recommendations.length && (
          <TreatmentRecommendationPill
            /**
             * If we have more than one, just show the first one
             */
            recommendation={treatment.recommendations[0]}
          />
        )}
      </div>

      <div className="space-y-2">
        <div className="flex">
          <button
            onClick={() => onTreatmentTitleClick()}
            className="flex items-center text-left"
          >
            <S2Typography
              variant="headline-6-alt"
              textColorVariant="white"
              translatedContent={language[
                TREATMENT_MESSAGE_IDS[treatment.type]
              ]()}
            />

            <IconChevronRightSvg svg={{ className: 'h-5 w-5' }} />
          </button>
        </div>

        <div className="mt-[10px] mb-[5px]">
          {levelDefinition && (
            <TreatmentMetaDetails
              levelDefinition={levelDefinition}
              focusArea={focusAreaDefinition}
              biasType={treatment.biasType}
            />
          )}
        </div>

        {treatment.projectedSavings && (
          <ProjectedSavingsCallout
            projectedSavings={treatment.projectedSavings}
          />
        )}
      </div>
    </div>
  );
};

interface SolutionCardStackProps {
  treatments: Treatment[];
  currentTreatmentIndex: number;
  onTreatmentTitleClick: () => void;
  pagination: React.ReactNode;
  allFocusAreaDefinitions: MyFocusAreaDefinition[];
}

export const SolutionCardStack: React.FC<SolutionCardStackProps> = ({
  treatments,
  currentTreatmentIndex,
  pagination,
  onTreatmentTitleClick,
  allFocusAreaDefinitions,
}) => {
  return (
    treatments.length > 0 && (
      <div
        className={cn(
          'bg-white rounded-lg shadow-2px p-6',
          !treatments[currentTreatmentIndex] && 'invisible',
          'flex-1 space-y-5 relative min-h-[18rem] bg-tertiary text-white font-sans',
        )}
        style={{
          background: solutionCardGradient(treatments[currentTreatmentIndex]),
        }}
      >
        <SolutionCard
          treatment={treatments[currentTreatmentIndex]}
          onTreatmentTitleClick={onTreatmentTitleClick}
          allFocusAreaDefinitions={allFocusAreaDefinitions}
        />

        {treatments.length > 1 && (
          <div className="absolute bottom-4 left-5">{pagination}</div>
        )}
      </div>
    )
  );
};
